/**
 * Utility functions for generating company domain names from company names
 */

/**
 * Common company suffixes to remove when generating domain names
 */
const COMPANY_SUFFIXES = [
  'Pte Ltd',
  'Pte. Ltd.',
  'Pte. Ltd',
  'Pvt Ltd',
  'Pvt. Ltd.',
  'Pvt. Ltd',
  'Private Limited',
  'Inc',
  'Inc.',
  'Incorporated',
  'LLC',
  'L.L.C.',
  'Ltd',
  'Ltd.',
  'Limited',
  'Corporation',
  'Corp',
  'Corp.',
  'Co',
  'Co.',
  'Company',
  'LLP',
  'L.L.P.',
  'Limited Liability Partnership',
  'GmbH',
  'AG',
  'S.A.',
  'SA',
  'S.A.R.L.',
  'SARL',
  'B.V.',
  'BV',
  'N.V.',
  'NV',
  'Pty Ltd',
  'Pty. Ltd.',
  'PLC',
  'P.L.C.',
] as const;

/**
 * Generates a domain name from a company name
 *
 * @param companyName - The full company name
 * @returns The generated domain name (e.g., "example.com")
 *
 * @example
 * generateDomain("ABC Pte Ltd") // Returns "abc.com"
 * generateDomain("My Company Inc") // Returns "mycompany.com"
 * generateDomain("ABC") // Returns "abc.com"
 */
export function generateDomain(companyName: string): string {
  if (!companyName || companyName.trim() === '') {
    return '';
  }

  // Start with the company name
  let domain = companyName.trim();

  // Remove company suffixes (case-insensitive)
  for (const suffix of COMPANY_SUFFIXES) {
    const regex = new RegExp(`\\b${suffix}\\b`, 'gi');
    domain = domain.replace(regex, '');
  }

  // Remove special characters and keep only alphanumeric and spaces
  domain = domain.replace(/[^a-zA-Z0-9\s]/g, '');

  // Remove extra spaces and convert to lowercase
  domain = domain.trim().replace(/\s+/g, '').toLowerCase();

  // If domain is empty after processing, return empty string
  if (domain === '') {
    return '';
  }

  // Append .com
  return `${domain}.com`;
}
